import { legacyAccept, optionalJsonOrText } from './helpers'
import { internalApiserverApi } from './internal-apiserver'

// The customer app reads these notes too (`GET analyses/notes/{gameId}` on a game's features).
export type AnalysisNote = {
  id?: string
  gameId: string
  // 0 is the game's own note.
  featureLegacyId: number
  notes: string
  organizationId?: string
  [key: string]: unknown
}

// Feature legacy id → note.
export type AnalysisNotesMap = Record<string, AnalysisNote>

export type AnalysisNoteRef = Pick<AnalysisNote, 'gameId' | 'featureLegacyId'>

export type AnalysisNoteWriteResult = { stored: AnalysisNote | null }

export const gameNoteLegacyId = 0

// Every note write sends `legacyAccept`; the update and delete answer with an empty body.

// A feature without a note answers with an empty body or with `""`.
const toNote = (answer: unknown) => (answer && typeof answer === 'object' ? (answer as AnalysisNote) : null)

const noteRequest = ({ gameId, featureLegacyId }: AnalysisNoteRef) => ({
  url: `analyses/notes/${gameId}/${featureLegacyId}`,
  responseHandler: optionalJsonOrText,
})
const notesTag = (gameId: string) => ({ type: 'analysisNotes' as const, id: gameId })

const analysisNotesApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['analysisNotes'] }).injectEndpoints({
  endpoints: (builder) => ({
    getAnalysisNotes: builder.query<AnalysisNotesMap, string>({
      query: (gameId) => ({ url: `analyses/notes/${gameId}`, responseHandler: optionalJsonOrText }),
      transformResponse: (map: unknown) => (map && typeof map === 'object' ? (map as AnalysisNotesMap) : {}),
      providesTags: (_result, _error, gameId) => [notesTag(gameId)],
    }),
    getAnalysisNote: builder.query<AnalysisNote | null, AnalysisNoteRef>({
      query: noteRequest,
      transformResponse: toNote,
      providesTags: (_result, _error, { gameId }) => [notesTag(gameId)],
    }),
    // Legacy's bodies: a new note without an `id` is posted, a stored one is put whole. Read back from the game.
    saveAnalysisNote: builder.mutation<AnalysisNoteWriteResult, AnalysisNote>({
      async queryFn(note, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery({
          url: 'analyses/notes',
          method: note.id ? 'PUT' : 'POST',
          body: note,
          headers: legacyAccept,
          responseHandler: optionalJsonOrText,
        })
        if (saved.error) return { error: saved.error }
        const stored = await baseQuery(noteRequest(note))
        if (stored.error) return { error: stored.error }
        return { data: { stored: toNote(stored.data) } }
      },
      invalidatesTags: (_result, _error, { gameId }) => [notesTag(gameId)],
    }),
    deleteAnalysisNote: builder.mutation<AnalysisNoteWriteResult, AnalysisNote & { id: string }>({
      async queryFn(note, _api, _extraOptions, baseQuery) {
        const deleted = await baseQuery({ url: `analyses/notes/${note.id}`, method: 'DELETE', headers: legacyAccept, responseHandler: optionalJsonOrText })
        if (deleted.error) return { error: deleted.error }
        const stored = await baseQuery(noteRequest(note))
        if (stored.error) return { error: stored.error }
        return { data: { stored: toNote(stored.data) } }
      },
      invalidatesTags: (_result, _error, { gameId }) => [notesTag(gameId)],
    }),
  }),
})

export const { useGetAnalysisNotesQuery, useGetAnalysisNoteQuery, useLazyGetAnalysisNoteQuery, useSaveAnalysisNoteMutation, useDeleteAnalysisNoteMutation } =
  analysisNotesApi
